<template>
  <!--
    FINAL CHAT V2 -- the tutor's pending question (`session.pendingAsk`),
    answered the way the current upstream defines: a click sends
    `resume: {optionId}` and the paused tutor turn continues from where it
    stopped. The question itself is the tutor bubble above; this card holds
    what the student answers with. Same V2 card family as the suggestions
    (`.related-paths`).

    - `disambiguate` (which lesson was meant) and `intake` (what the request
      needs to know): one V2 choice card per upstream option.
    - `confirm` (upstream design A8 -- every photo is read back before the
      tutor works from it): the text read from the photo, with «درست است»
      and «ویرایش می‌کنم». Edit opens the reading for correction; the
      corrected text travels as `resume.text`. Nothing is diagnosed from an
      unconfirmed reading, so this is the photo's analysis step.

    Typing in the composer instead stays possible (upstream then starts a new
    turn, as in the reference console).
  -->
  <section
    class="ai-tutor-ask"
    :class="`ai-tutor-ask--${ask.kind}`"
    data-testid="ai-tutor-ask"
    :aria-label="title"
  >
    <div class="ai-tutor-ask__head">
      <div>
        <student-home-icon :name="isConfirm ? 'camera' : 'spark'" :size="20" />
        <span>
          <strong>{{ title }}</strong>
          <small>{{ hint }}</small>
        </span>
      </div>
    </div>

    <template v-if="isConfirm">
      <div
        v-if="!editing"
        class="ai-tutor-ask__draft"
        data-testid="ai-tutor-ask-draft"
      >
        <markdown-content v-if="draft" :source="draft" />
        <p v-else class="ai-tutor-ask__draft-empty">
          {{ $t('aiTutorSetup.chat.ask.emptyDraft') }}
        </p>
      </div>
      <label v-else class="ai-tutor-ask__editor">
        <span>{{ $t('aiTutorSetup.chat.ask.editLabel') }}</span>
        <textarea
          ref="editor"
          v-model="edited"
          dir="auto"
          rows="4"
          data-testid="ai-tutor-ask-editor"
          :disabled="disabled"
          :placeholder="$t('aiTutorSetup.chat.ask.editPlaceholder')"
        ></textarea>
      </label>

      <div class="ai-tutor-ask__actions">
        <template v-if="editing">
          <button
            type="button"
            class="ai-tutor-ask__primary"
            data-testid="ai-tutor-ask-send-edit"
            :disabled="disabled || !edited.trim()"
            @click="sendEdit"
          >
            <student-home-icon name="send" :size="15" />
            {{ $t('aiTutorSetup.chat.ask.sendEdit') }}
          </button>
          <button
            type="button"
            class="ai-tutor-ask__secondary"
            data-testid="ai-tutor-ask-cancel-edit"
            :disabled="disabled"
            @click="editing = false"
          >
            {{ $t('aiTutorSetup.chat.ask.cancelEdit') }}
          </button>
        </template>
        <template v-else>
          <button
            v-for="(option, index) in options"
            :key="option.id"
            type="button"
            :class="
              index === 0 ? 'ai-tutor-ask__primary' : 'ai-tutor-ask__secondary'
            "
            :data-testid="`ai-tutor-ask-option-${option.id}`"
            :disabled="disabled"
            @click="choose(option)"
          >
            <student-home-icon
              v-if="option.id === 'confirm'"
              name="check"
              :size="15"
            />
            {{ option.label }}
          </button>
        </template>
      </div>
    </template>

    <div
      v-else
      class="ai-tutor-ask__choices"
      :style="{ '--ai-tutor-ask-columns': Math.min(options.length, 3) || 1 }"
    >
      <button
        v-for="option in options"
        :key="option.id"
        type="button"
        :class="{ selected: option.id === selectedOptionId }"
        :data-testid="`ai-tutor-ask-option-${option.id}`"
        :disabled="disabled"
        :aria-pressed="option.id === selectedOptionId ? 'true' : 'false'"
        @click="choose(option)"
      >
        <strong>{{ option.label }}</strong>
        <student-home-icon name="arrow" :size="17" />
      </button>
    </div>
  </section>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import MarkdownContent from './MarkdownContent.vue'

// The upstream confirm-transcription node's own option ids.
const EDIT_OPTION = 'edit'

export default {
  name: 'ChatAsk',
  components: { StudentHomeIcon, MarkdownContent },
  props: {
    /** @type {import('vue').PropOptions<import('@/services/aiTutor/domain').TutorPendingAction>} */
    ask: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // The option whose answer is on its way, if any.
    selectedOptionId: {
      type: String,
      default: '',
    },
  },
  data() {
    const draft = (this.ask && this.ask.draft) || ''
    return {
      // An empty reading opens straight into writing the text.
      editing: this.ask && this.ask.kind === 'confirm' && !draft.trim(),
      edited: draft,
    }
  },
  computed: {
    isConfirm() {
      return this.ask.kind === 'confirm'
    },
    draft() {
      return (this.ask.draft || '').trim()
    },
    options() {
      return Array.isArray(this.ask.options)
        ? this.ask.options.filter(option => option && option.id)
        : []
    },
    title() {
      return this.isConfirm
        ? this.$t('aiTutorSetup.chat.ask.confirmTitle')
        : this.$t('aiTutorSetup.chat.ask.chooseTitle')
    },
    hint() {
      return this.isConfirm
        ? this.$t('aiTutorSetup.chat.ask.confirmHint')
        : this.$t('aiTutorSetup.chat.ask.chooseHint')
    },
  },
  methods: {
    choose(option) {
      if (this.disabled) return
      if (this.isConfirm && option.id === EDIT_OPTION) {
        this.edited = this.draft
        this.editing = true
        this.$nextTick(() => {
          const editor = this.$refs.editor
          if (editor && typeof editor.focus === 'function') editor.focus()
        })
        return
      }
      this.$emit('answer', { optionId: option.id, label: option.label })
    },
    sendEdit() {
      const text = this.edited.trim()
      if (this.disabled || !text) return
      const option = this.options.find(item => item.id === EDIT_OPTION)
      this.$emit('answer', {
        optionId: EDIT_OPTION,
        label: option ? option.label : '',
        text,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// V2 card family (sha256 6ddb695a...): the frame, head and choice cards are
// `.related-paths`; the confirm actions are `.compact-feedback`'s buttons;
// the editor follows V2's form fields (`.field textarea`).
$v2-card-inset: 43px;

.ai-tutor-ask {
  margin: 0 $v2-card-inset;
  padding: 16px;
  border: 1px solid $borna-border;
  border-radius: 19px;
  background: #fff;
  line-height: 1.75;

  &--confirm {
    border-color: $borna-indigo-100;
    background: linear-gradient(145deg, #f8faff, #fff);
  }
}

.ai-tutor-ask__head > div {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  > ::v-deep svg {
    flex: none;
    color: $borna-indigo-600;
  }

  > span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    color: $borna-text;
    font-size: 11px;
  }

  small {
    color: $borna-muted;
    font-size: 9px;
  }
}

.ai-tutor-ask__draft {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid $borna-border;
  border-radius: 13px;
  background: $borna-canvas;
  color: $borna-text;
  font-size: 11px;

  // UX2-04 (doc 30 §4): math in the 11px reading text is pinned to the
  // measured-good 14.5px (MarkdownContent's 1.21em of 11px = 13.31px would
  // sit in the band where `=` loses a bar at DPR 1).
  ::v-deep .ai-md .katex {
    font-size: 14.5px;
  }
}

.ai-tutor-ask__draft-empty {
  margin: 0;
  color: $borna-muted;
}

.ai-tutor-ask__editor {
  display: grid;
  gap: 6px;
  margin-top: 12px;

  > span {
    color: $borna-text;
    font-size: 10px;
    font-weight: 800;
  }

  textarea {
    box-sizing: border-box;
    width: 100%;
    min-height: 96px;
    padding: 10px 12px;
    border: 1px solid $borna-indigo-300;
    border-radius: 13px;
    outline: 0;
    background: #fff;
    color: $borna-text;
    font-family: inherit;
    font-size: 11px;
    line-height: 1.8;
    resize: vertical;

    &:focus {
      border-color: $borna-indigo-600;
      box-shadow: 0 0 0 4px rgba(49, 75, 214, 0.07);
    }
  }
}

.ai-tutor-ask__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-height: 32px;
    padding: 0 12px;
    border-radius: 9px;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
    cursor: pointer;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }
}

.ai-tutor-ask__primary {
  border: 0;
  background: $borna-indigo-600;
  color: #fff;
}

.ai-tutor-ask__secondary {
  border: 1px solid $borna-border;
  background: #fff;
  color: $borna-text;
}

.ai-tutor-ask__choices {
  display: grid;
  grid-template-columns: repeat(var(--ai-tutor-ask-columns, 3), 1fr);
  gap: 7px;
  margin-top: 12px;

  button {
    display: grid;
    grid-template-columns: 1fr 17px;
    align-items: center;
    gap: 7px;
    min-width: 0;
    min-height: 56px;
    padding: 10px;
    border: 1px solid $borna-border;
    border-radius: 13px;
    background: $borna-canvas;
    color: $borna-text;
    font-family: inherit;
    line-height: 1.65;
    text-align: start;
    cursor: pointer;

    > strong {
      min-width: 0;
      font-size: 9px;
      overflow-wrap: anywhere;
    }

    > ::v-deep svg {
      color: $borna-muted;
    }

    &:hover:not(:disabled),
    &.selected {
      border-color: $borna-indigo-600;
      background: $borna-indigo-50;

      > strong,
      > ::v-deep svg {
        color: $borna-indigo-900;
      }
    }

    &:disabled {
      cursor: not-allowed;
    }

    &:disabled:not(.selected) {
      opacity: 0.55;
    }
  }
}

html:not(.is-rtl) .ai-tutor-ask__choices button > ::v-deep svg {
  transform: scaleX(-1);
}

// V2 `@media (width<=620px)`: full width, one card per row, full-width
// actions.
@media (max-width: 620px) {
  .ai-tutor-ask {
    margin-inline: 0;
  }

  .ai-tutor-ask__choices {
    grid-template-columns: 1fr;
  }

  .ai-tutor-ask__actions button {
    flex: 1;
  }
}
</style>
